import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import useAppLanguage from "../hooks/useAppLanguage";
import { translateSurvey } from "../utils/lang";
import LanguageContext from "./language";
import UserContext from "./user";

const CartContext = createContext({
  campaign: undefined,
  status: undefined,
  error: undefined,
  navigation: undefined,
  showDetail: undefined,
  formData: undefined,
  partial: undefined,
  hideMyBooking: undefined,
  setPartial: () => 0,
  cartRequest: undefined,
  setFormData: () => 0,
  currentSectionIndex: undefined,
  slotNotAvailable: undefined,
  survey: undefined,
  labels: undefined,
  appLang: undefined,
  setAppLang: () => 0,
  languages: undefined,
  setNavigation: () => 0,
  setShowDetail: () => 0,
  setCurrentSectionIndex: () => 0,
  goToCartDetail: () => 0,
  handleNext: () => 0,
  setCartRequest: () => 0,
  setSlotNotAvailable: () => 0,
  setHideMyBooking: () => 0,
});

export const CartProvider = ({ children }) => {
  const { callApi, logged, userProfile } = useContext(UserContext);
  const { language, defaultLabels, unloggedLanguage } = useContext(LanguageContext);
  const [navigation, setNavigation] = useState();
  const [showDetail, setShowDetail] = useState();
  const [currentSectionIndex, setCurrentSectionIndex] = useState();
  const [formData, setFormData] = useState({});
  const [partial, setPartial] = useState(false);
  const [cartRequest, setCartRequest] = useState();
  const [status, setStatus] = useState();
  const [slotNotAvailable, setSlotNotAvailable] = useState();
  const [error, setError] = useState();
  const [hideMyBooking, setHideMyBooking] = useState();
  const locationRef = useRef();

  const { data: campaign } = useQuery(
    ["campaign", JSON.stringify(userProfile)],
    () => callApi("/cart/campaign/latest"),
    {
      enabled: !!userProfile && !navigation,
      onSuccess: (data) => {
        // TamTamy campaign statuses
        //     DRAFT,
        //     READY,
        //     CART_SELECTION,
        //     WAITING_FOR_BOOKING_OPENING,
        //     BOOKING_DELIVERY,
        //     DELIVERY,
        //     ENDED;
        if (
          data?.cartSelectionStartDate &&
          data?.cartSelectionEndDate &&
          data?.bookingDeliveryStartDate &&
          data?.bookingDeliveryEndDate &&
          data?.status
        ) {
          const today = dayjs();
          if (
            today.isAfter(data.cartSelectionStartDate, "minute") &&
            today.isBefore(data.cartSelectionEndDate, "minute") &&
            data.status === "CART_SELECTION"
          ) {
            setStatus("cart-selection");
          } else if (
            today.isAfter(data.cartSelectionEndDate, "minute") &&
            today.isBefore(data.bookingDeliveryStartDate, "minute") &&
            data.status === "WAITING_FOR_BOOKING_OPENING"
          ) {
            setStatus("cart-selection-closed");
          } else if (
            today.isAfter(data.bookingDeliveryStartDate, "minute") &&
            today.isBefore(data.bookingDeliveryEndDate, "minute") &&
            data.status === "BOOKING_DELIVERY"
          ) {
            setStatus("slot-booking");
          } else {
            setStatus("closed");
          }
        } else {
          setStatus("invalid-campaign");
        }
      },
      onError: (error) => setError(error),
    }
  );

  const { data: survey } = useQuery(
    ["cart-survey", campaign?.id, JSON.stringify(userProfile)],
    () => callApi(`/cart/campaign/${campaign.id}/survey`),
    {
      enabled: !!userProfile && !navigation && !!campaign?.id,
      select: ({ definition, ...data }) => {
        return {
          ...data,
          definition: {
            ...definition,
            sections: definition.sections.sort((a, b) =>
              a.position > b.position ? 1 : a.position < b.position ? -1 : 0
            ),
          },
        };
      },
      onError: (error) => setError(error),
    }
  );

  const handlePrevious = useCallback(() => {
    setCurrentSectionIndex((current) => current - 1);
  }, []);

  const handleNext = useCallback((submissionId) => {
    if (submissionId) {
      setPartial(submissionId);
    }
    setCurrentSectionIndex((current) => current + 1);
  }, []);

  const { appLang, setAppLang, languages, labels } = useAppLanguage(
    defaultLabels,
    !!survey && language,
    survey,
    logged && (error ? error.status !== 403 : true) && status !== "invalid-campaign"
      ? null
      : unloggedLanguage
  );

  const translatedSurvey = useMemo(() => {
    if (survey) {
      return translateSurvey(survey, appLang);
    }
  }, [survey, appLang]);

  const goToCartDetail = useCallback((e) => {
    const id = e?.currentTarget?.id?.split("-")?.[0];
    if (id) {
      setShowDetail(id);
    }
  }, []);

  useEffect(() => {
    if (!logged) {
      setError(null);
    }
  }, [logged]);

  useEffect(() => {
    const currentLocation = navigation + "-" + currentSectionIndex + "-" + showDetail;
    if (locationRef.current !== currentLocation) {
      locationRef.current = currentLocation;
      window.scrollTo(0, 0);
    }
  }, [navigation, currentSectionIndex, showDetail]);

  const contextValue = useMemo(
    () => ({
      campaign,
      status,
      error,
      navigation,
      formData,
      partial,
      setPartial,
      setFormData,
      cartRequest,
      showDetail,
      currentSectionIndex,
      slotNotAvailable,
      survey: translatedSurvey,
      appLang,
      labels,
      hideMyBooking,
      setAppLang,
      languages,
      setStatus,
      setNavigation,
      setShowDetail,
      setCurrentSectionIndex,
      goToCartDetail,
      handleNext,
      setCartRequest,
      setSlotNotAvailable,
      setHideMyBooking,
      handlePrevious,
    }),
    [
      campaign,
      status,
      error,
      navigation,
      formData,
      partial,
      hideMyBooking,
      setPartial,
      setFormData,
      cartRequest,
      showDetail,
      currentSectionIndex,
      slotNotAvailable,
      translatedSurvey,
      appLang,
      labels,
      setAppLang,
      languages,
      setStatus,
      setNavigation,
      setShowDetail,
      setCurrentSectionIndex,
      goToCartDetail,
      handleNext,
      setCartRequest,
      setSlotNotAvailable,
      setHideMyBooking,
      handlePrevious,
    ]
  );

  return <CartContext.Provider value={contextValue}>{children}</CartContext.Provider>;
};

export default CartContext;
